import { Button } from '@heidi/design-system/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { LightBulb } from '@heidi/design-system/icons';
import { cn } from '@heidi/utils';
import { ChevronDown, X } from 'lucide-react';
import { useIntl } from 'react-intl';

import type { VoiceEvidenceSource } from '../../../contracts/voice-protocol';
import { voiceAssistantMessages as messages } from './voice-assistant.messages';
import { safeHttpHref, splitEvidenceKeypointLines } from './voiceEvidenceCardUtils';

export function VoiceEvidenceSources({
  sources,
  className,
}: {
  sources: VoiceEvidenceSource[];
  className?: string;
}) {
  const intl = useIntl();
  return (
    <Collapsible className={cn('group/sources', className)}>
      <CollapsibleTrigger
        type="button"
        className="flex w-full items-center gap-1 text-pretty text-xs font-medium uppercase tracking-wide text-text-tertiary"
      >
        <span>
          {intl.formatMessage(messages.sourcesHeadingWithCount, {
            count: sources.length,
          })}
        </span>
        <ChevronDown className="size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]/sources:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-1.5 space-y-1 text-xs text-text-secondary">
          {sources.map((source, index) => {
            const href = safeHttpHref(source.url);
            const label =
              source.title ||
              source.url ||
              source.domain ||
              intl.formatMessage(messages.sourceFallbackLabel);
            const key = `${source.url || label}-${index}`;
            return (
              <li key={key} className="leading-snug">
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-border underline-offset-2 hover:text-text-primary"
                  >
                    {label}
                  </a>
                ) : (
                  label
                )}
              </li>
            );
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

export interface VoiceEvidenceCardProps {
  text?: string;
  sources?: VoiceEvidenceSource[];
  className?: string;
  /** When set, shows an X to dismiss the dock callout. */
  onDismiss?: () => void;
}

function EvidenceBody({ text }: { text: string }) {
  const lines = splitEvidenceKeypointLines(text);
  if (lines.length === 0) return null;

  if (lines.length === 1) {
    return (
      <p dir="auto" className="text-pretty text-sm font-normal leading-normal">
        {lines[0]}
      </p>
    );
  }

  return (
    <ul
      dir="auto"
      className="list-disc ps-5 text-sm font-normal leading-normal [&>li:first-child]:!mt-0 [&>li:not(:first-child)]:!mt-2"
    >
      {lines.map((line, index) => (
        <li key={`${index}-${line}`} dir="auto" className="font-normal leading-normal">
          {line}
        </li>
      ))}
    </ul>
  );
}

/** Key Points–styled Evidence callout for Talk to Heidi dock highlights. */
export function VoiceEvidenceCard({
  text,
  sources,
  className,
  onDismiss,
}: VoiceEvidenceCardProps) {
  const intl = useIntl();
  const trimmed = text?.trim() ?? '';
  const showText = Boolean(trimmed);
  const showSources = Boolean(sources && sources.length > 0);
  if (!showText && !showSources) return null;

  return (
    <div
      data-testid="voice-evidence-card"
      className={cn(
        'flex max-h-[min(50vh,24rem)] min-h-0 min-w-0 max-w-[min(28rem,92vw)] flex-col overflow-hidden rounded-xl bg-validation-info-muted',
        className
      )}
    >
      <div className="flex shrink-0 items-center gap-1 px-4 pb-1 pt-5">
        <LightBulb size={18} className="text-skies-950 newBrandDark:text-text-primary" />
        <span className="min-w-0 flex-1 font-exposure text-base font-medium tracking-tight text-skies-950 newBrandDark:text-text-primary">
          {intl.formatMessage(messages.keyPoints)}
        </span>
        {onDismiss ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={intl.formatMessage(messages.dismissEvidence)}
            onClick={onDismiss}
            className="shrink-0 text-text-tertiary hover:text-text-primary"
            data-testid="voice-evidence-dismiss"
          >
            <X className="size-4" />
          </Button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-4 pb-5 pt-2">
        {showText ? (
          <div className="text-skies-950 newBrandDark:text-text-primary">
            <EvidenceBody text={trimmed} />
          </div>
        ) : null}

        {showSources && sources ? (
          <VoiceEvidenceSources sources={sources} className="mt-0" />
        ) : null}
      </div>
    </div>
  );
}
